import {
  IconAndNameEditorMenu,
  IconButton,
  Menu,
  MenuItem,
  type MenuProps,
  MenuSeparator,
  MenuSub,
  notify,
  Scrollable,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { inferOpenMode } from '@affine/core/utils';
import { useI18n } from '@affine/i18n';
import {
  DualLinkIcon,
  FolderIcon,
  InformationIcon,
  MoreHorizontalIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';
import { of } from 'rxjs';

import {
  AccountTemplateService,
  AccountTemplateTransferService,
} from '../../account-template';
import { AccountTemplateSection } from '../../account-template/view/account-template-section';
import { type DocRecord, DocsService } from '../../doc';
import { DocDisplayMetaService } from '../../doc-display-meta';
import { OrganizeService } from '../../organize';
import type { FolderNode } from '../../organize/entities/folder-node';
import { WorkbenchLink, WorkbenchService } from '../../workbench';
import { FolderTemplateService } from '../services/folder-template';
import { TemplateDocService } from '../services/template-doc';
import * as styles from './styles.css';

interface CommonProps {
  onSelect?: (docId: string) => void;
  asLink?: boolean;
}

interface DocItemProps extends CommonProps {
  doc: DocRecord;
  onChanged?: () => void;
}

const stopPointer = (e: React.PointerEvent) => e.stopPropagation();
const stopClick = (e: React.MouseEvent) => e.stopPropagation();

const DocActionsMenu = ({
  doc,
  onChanged,
}: {
  doc: DocRecord;
  onChanged?: () => void;
}) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const docDisplayService = useService(DocDisplayMetaService);
  const workbench = useService(WorkbenchService).workbench;
  const accountTemplateService = useService(AccountTemplateService);
  const transferService = useService(AccountTemplateTransferService);
  const accountAvailable = useLiveData(accountTemplateService.list.available$);
  const title = useLiveData(docDisplayService.title$(doc.id));
  const [renameOpen, setRenameOpen] = useState(false);

  const handleRename = useAsyncCallback(
    async (newName: string) => {
      await docsService.changeDocTitle(doc.id, newName);
    },
    [docsService, doc.id]
  );

  // promote a project template into the account-level library
  const handlePromote = useAsyncCallback(async () => {
    try {
      const result = await transferService.publish(doc.id);
      notify.success({
        title: t['com.affine.account-template.published-notify.title'](),
        message:
          result.skippedBlobs > 0
            ? t['com.affine.account-template.skipped-blobs.description']({
                count: String(result.skippedBlobs),
              })
            : result.hasCrossDocReferences
              ? t['com.affine.account-template.cross-doc-refs.description']()
              : undefined,
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: t['com.affine.account-template.failed-notify.title'](),
      });
    }
  }, [transferService, doc.id, t]);

  return (
    <>
      <Menu
        items={
          <>
            <MenuItem
              onClick={() => {
                workbench.openDoc(doc.id);
              }}
            >
              {t['com.affine.template-list.action.open']()}
            </MenuItem>
            {accountAvailable ? (
              <MenuItem
                onClick={handlePromote}
                data-testid={`template-doc-promote-${doc.id}`}
              >
                {t['com.affine.account-template.promote']()}
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => {
                setRenameOpen(true);
              }}
            >
              {t['com.affine.template-list.action.rename']()}
            </MenuItem>
            <MenuItem
              onClick={() => {
                void doc.setProperty('isTemplate', false);
                onChanged?.();
              }}
            >
              {t['com.affine.template-list.remove-from-templates']()}
            </MenuItem>
            <MenuItem
              type="danger"
              onClick={() => {
                void doc.moveToTrash();
                onChanged?.();
              }}
            >
              {t['com.affine.template-list.move-to-trash']()}
            </MenuItem>
          </>
        }
      >
        <IconButton
          size="16"
          className={styles.itemActions}
          data-testid={`template-doc-actions-${doc.id}`}
          onPointerDown={stopPointer}
          onClick={stopClick}
        >
          <MoreHorizontalIcon />
        </IconButton>
      </Menu>
      <IconAndNameEditorMenu
        open={renameOpen}
        onOpenChange={setRenameOpen}
        name={title}
        onNameChange={handleRename}
        inputTestId="template-doc-rename-input"
      >
        {null}
      </IconAndNameEditorMenu>
    </>
  );
};

const DocItem = ({ doc, onSelect, asLink, onChanged }: DocItemProps) => {
  const docDisplayService = useService(DocDisplayMetaService);
  const Icon = useLiveData(docDisplayService.icon$(doc.id));
  const title = useLiveData(docDisplayService.title$(doc.id));

  const onClick = useAsyncCallback(async () => {
    onSelect?.(doc.id);
  }, [doc.id, onSelect]);

  const menuItem = (
    <MenuItem
      prefixIcon={<Icon />}
      onClick={onClick}
      suffix={<DocActionsMenu doc={doc} onChanged={onChanged} />}
      data-testid={`template-doc-item-${doc.id}`}
    >
      {title}
    </MenuItem>
  );

  if (asLink) {
    return <WorkbenchLink to={`/${doc.id}`}>{menuItem}</WorkbenchLink>;
  }
  return menuItem;
};

const Empty = () => {
  const t = useI18n();
  return (
    <div className={styles.empty}>
      <InformationIcon className={styles.emptyIcon} />
      <span className={styles.emptyText}>
        {t['com.affine.template-list.empty']()}
      </span>
      <div className={styles.space} />
      <a
        href="https://affine.pro/blog/how-to-use-template"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        <IconButton icon={<DualLinkIcon />} />
      </a>
    </div>
  );
};

const FolderTemplateActions = ({
  folderId,
  node,
  onChanged,
}: {
  folderId: string;
  node: FolderNode | null;
  onChanged: () => void;
}) => {
  const t = useI18n();
  const organizeService = useService(OrganizeService);
  const docsService = useService(DocsService);
  const templateDocService = useService(TemplateDocService);
  const [renameOpen, setRenameOpen] = useState(false);

  const handleRename = useCallback(
    (newName: string) => {
      node?.rename(newName);
    },
    [node]
  );

  const handleDelete = useAsyncCallback(async () => {
    const docIds = organizeService.collectDocIds(folderId);
    for (const id of docIds) {
      try {
        const record = docsService.list.doc$(id).value;
        record?.moveToTrash();
      } catch (err) {
        console.warn('[template-doc] failed to trash doc', id, err);
      }
    }
    node?.delete();
    onChanged?.();
  }, [organizeService, docsService, node, folderId, onChanged]);

  return (
    <>
      <Menu
        items={
          <>
            <MenuItem
              onClick={() => {
                setRenameOpen(true);
              }}
            >
              {t['com.affine.template-list.action.rename']()}
            </MenuItem>
            <MenuItem
              onClick={() => {
                templateDocService.folderList.setTemplate(folderId, false);
                onChanged();
              }}
            >
              {t['com.affine.template-list.remove-from-templates']()}
            </MenuItem>
            <MenuItem type="danger" onClick={handleDelete}>
              {t['com.affine.template-list.delete-folder']()}
            </MenuItem>
          </>
        }
      >
        <IconButton
          size="16"
          className={styles.itemActions}
          data-testid={`template-folder-actions-${folderId}`}
          onPointerDown={stopPointer}
          onClick={stopClick}
        >
          <MoreHorizontalIcon />
        </IconButton>
      </Menu>
      <IconAndNameEditorMenu
        open={renameOpen}
        onOpenChange={setRenameOpen}
        name={node?.name$.value ?? 'Untitled'}
        onNameChange={handleRename}
        inputTestId="template-folder-rename-input"
      >
        {null}
      </IconAndNameEditorMenu>
    </>
  );
};

interface FolderTemplateSubContentProps extends CommonProps {
  folderId: string;
  onInstantiated: (folderId: string) => void;
  onChanged: () => void;
}

/**
 * Content of the expandable folder-template submenu: an action to
 * instantiate the whole folder, followed by the docs stored directly in
 * the folder (each can be duplicated on its own).
 */
const FolderTemplateSubContent = ({
  folderId,
  onSelect,
  asLink,
  onInstantiated,
  onChanged,
}: FolderTemplateSubContentProps) => {
  const t = useI18n();
  const organizeService = useService(OrganizeService);
  const folderTemplateService = useService(FolderTemplateService);
  const docsService = useService(DocsService);
  const docDisplayService = useService(DocDisplayMetaService);
  const node = useLiveData(organizeService.folderTree.folderNode$(folderId));

  const handleInstantiate = useAsyncCallback(async () => {
    const newFolderId = await folderTemplateService.instantiate(folderId, null);
    notify.success({
      title: t['com.affine.folder-template.created-notify.title'](),
    });
    onInstantiated(newFolderId);
  }, [folderTemplateService, folderId, onInstantiated, t]);

  const children$ = useMemo(
    () => (node ? node.children$ : LiveData.from(of([] as FolderNode[]), [])),
    [node]
  );
  const childNodes = useLiveData(children$);
  const docChildren = childNodes.filter(child => child.type$.value === 'doc');

  const docItems = docChildren
    .map(child => {
      const docId = child.id;
      if (!docId) return null;
      const doc = docsService.list.doc$(docId).value;
      if (!doc || doc.trash$.value) return null;
      return {
        doc,
        title: docDisplayService.title$(docId).value,
      };
    })
    .filter((item): item is { doc: DocRecord; title: string } => Boolean(item))
    .sort((a, b) => a.title.localeCompare(b.title));

  if (!node) {
    return null;
  }

  return (
    <>
      <MenuItem
        prefixIcon={<TemplateIcon />}
        onClick={handleInstantiate}
        data-testid={`template-folder-use-${folderId}`}
      >
        {t['com.affine.template-list.use-folder-template']()}
      </MenuItem>
      {docItems.length > 0 && <MenuSeparator />}
      {docItems.map(({ doc }) => (
        <DocItem
          key={doc.id}
          doc={doc}
          onSelect={onSelect}
          asLink={asLink}
          onChanged={onChanged}
        />
      ))}
    </>
  );
};

interface TemplateListMenuContentProps extends CommonProps {
  prefixItems?: React.ReactNode;
  suffixItems?: React.ReactNode;
  onFolderInstantiated?: (folderId: string) => void;
  /**
   * When provided, the account-level ("shared") section is rendered. Callers
   * that persist the selection as a doc id (the default-template settings)
   * must leave it out — a shared template has no doc id in this workspace.
   */
  onSelectAccountTemplate?: (templateId: string) => void;
  /** doc offered as the source when re-publishing a shared template */
  republishDocId?: string;
}

export const TemplateListMenuContent = ({
  prefixItems,
  suffixItems,
  onFolderInstantiated,
  onSelectAccountTemplate,
  republishDocId,
  ...props
}: TemplateListMenuContentProps) => {
  const t = useI18n();
  const templateDocService = useService(TemplateDocService);
  const organizeService = useService(OrganizeService);
  const accountTemplateService = useService(AccountTemplateService);
  const accountAvailable = useLiveData(accountTemplateService.list.available$);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion(v => v + 1), []);

  const templateDocs = useMemo(
    () => templateDocService.list.getTemplateDocs(),
    // recompute after local mutations (remove / delete)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateDocService, version]
  );

  const templateFolderIds = useLiveData(
    templateDocService.folderList.templateFolderIds$
  );

  const templateFolders = useMemo(() => {
    return templateFolderIds
      .map(id => {
        const node = organizeService.folderTree.folderNode$(id).value;
        return { id, node };
      })
      .filter(
        (
          entry
        ): entry is { id: string; node: NonNullable<typeof entry.node> } =>
          !!entry.node
      )
      .sort((a, b) =>
        (a.node.name$.value ?? '').localeCompare(b.node.name$.value ?? '')
      );
  }, [templateFolderIds, organizeService]);

  // Standalone docs = template docs that live outside of any template folder.
  const standaloneDocs = useMemo(() => {
    const insideTemplateFolder = (docId: string) =>
      templateFolderIds.some(
        folderId =>
          organizeService.folderTree
            .folderNode$(folderId)
            .value?.contains(docId) ?? false
      );
    return templateDocs
      .filter(doc => !insideTemplateFolder(doc.id))
      .sort((a, b) => a.title$.value.localeCompare(b.title$.value));
  }, [templateDocs, templateFolderIds, organizeService]);

  const showAccountSection = !!onSelectAccountTemplate && accountAvailable;

  return (
    <ul className={styles.list}>
      {prefixItems}
      {showAccountSection ? (
        <div className={styles.sectionTitle}>
          {t['com.affine.template-list.section.workspace']()}
        </div>
      ) : null}
      {templateFolders.length === 0 && standaloneDocs.length === 0 ? (
        <Empty />
      ) : (
        <>
          {templateFolders.map(({ id, node }) => (
            <MenuSub
              key={id}
              triggerOptions={{
                prefixIcon: <FolderIcon />,
                suffix: (
                  <FolderTemplateActions
                    folderId={id}
                    node={node}
                    onChanged={refresh}
                  />
                ),
              }}
              subContentOptions={{ sideOffset: 16 }}
              items={
                <FolderTemplateSubContent
                  folderId={id}
                  onSelect={props.onSelect}
                  asLink={props.asLink}
                  onInstantiated={folderId => onFolderInstantiated?.(folderId)}
                  onChanged={refresh}
                />
              }
            >
              {node.name$.value || 'Untitled'}
            </MenuSub>
          ))}
          {templateFolders.length > 0 && standaloneDocs.length > 0 && (
            <MenuSeparator />
          )}
          {standaloneDocs.map(doc => (
            <DocItem key={doc.id} doc={doc} {...props} onChanged={refresh} />
          ))}
        </>
      )}
      {showAccountSection ? (
        <>
          <MenuSeparator />
          <div
            className={styles.sectionTitle}
            data-testid="template-section-account"
          >
            {t['com.affine.template-list.section.account']()}
          </div>
          <AccountTemplateSection
            onSelect={onSelectAccountTemplate}
            republishDocId={republishDocId}
          />
        </>
      ) : null}
      {suffixItems}
    </ul>
  );
};

export const TemplateListMenuContentScrollable = (
  props: TemplateListMenuContentProps
) => {
  return (
    <Scrollable.Root>
      <Scrollable.Scrollbar />
      <Scrollable.Viewport className={styles.scrollableViewport}>
        <TemplateListMenuContent {...props} />
      </Scrollable.Viewport>
    </Scrollable.Root>
  );
};

interface TemplateListMenuProps
  extends TemplateListMenuContentProps, Omit<MenuProps, 'items'> {}
export const TemplateListMenu = ({
  children,
  onSelect,
  asLink,
  prefixItems,
  suffixItems,
  onFolderInstantiated,
  onSelectAccountTemplate,
  republishDocId,
  contentOptions,
  ...otherProps
}: TemplateListMenuProps) => {
  return (
    <Menu
      items={
        <TemplateListMenuContentScrollable
          onSelect={onSelect}
          asLink={asLink}
          prefixItems={prefixItems}
          suffixItems={suffixItems}
          onFolderInstantiated={onFolderInstantiated}
          onSelectAccountTemplate={onSelectAccountTemplate}
          republishDocId={republishDocId}
        />
      }
      contentOptions={{
        ...contentOptions,
        className: styles.menuContent,
      }}
      {...otherProps}
    >
      {children}
    </Menu>
  );
};

export const TemplateListMenuAdd = () => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  const createNewTemplate = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const record = docsService.createDoc({ isTemplate: true });
      workbench.openDoc(record.id, { at: inferOpenMode(e) });
    },
    [docsService, workbench]
  );

  return (
    <MenuItem
      data-testid="template-doc-item-create"
      prefixIcon={<TemplateIcon />}
      onClick={createNewTemplate}
      onAuxClick={createNewTemplate}
    >
      {t['com.affine.template-list.create-new']()}
    </MenuItem>
  );
};
